/**
 * InvitationList Component
 * Displays pending organization invitations
 */
import { Invitation } from '../../api/organizations';
import Badge from '../common/Badge';
import { ClipboardDocumentIcon, EnvelopeIcon } from './icons';

interface InvitationListProps {
  invitations: Invitation[];
  loading?: boolean;
  onCancelInvitation: (invitationId: string) => void;
  onCopyLink: (invitationUrl: string) => void;
}

interface InvitationCardProps {
  invitation: Invitation;
  onCancel: (id: string) => void;
  onCopyLink: (url: string) => void;
}

function InvitationCard({ invitation, onCancel, onCopyLink }: InvitationCardProps) {
  // Calculate time until expiration
  const getExpiryText = () => {
    const expiresAt = new Date(invitation.expires_at);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    return `Expires in ${diffDays} days`;
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate days until expiration
  const daysUntilExpiry = () => {
    const expiresAt = new Date(invitation.expires_at);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const isExpiringSoon = daysUntilExpiry() <= 3 && daysUntilExpiry() > 0;
  const isExpired = !invitation.is_valid || daysUntilExpiry() < 0;

  // Mock invitation URL (will be provided by API in real implementation)
  const invitationUrl = `https://paraclete.ai/invite/${invitation.id}`;

  return (
    <div
      className={`border rounded-lg p-5 space-y-4 transition-all ${
        isExpired
          ? 'bg-dark-bg/30 border-dark-border/30 opacity-60'
          : 'bg-dark-card/50 border-dark-border/50'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-dark-bg/50 flex items-center justify-center">
            <EnvelopeIcon className="w-5 h-5 text-dark-text-muted" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-dark-text font-semibold">{invitation.email}</span>
              {isExpiringSoon && <Badge variant="warning" size="sm">EXPIRES SOON</Badge>}
              {isExpired && <Badge variant="danger" size="sm">EXPIRED</Badge>}
            </div>

            <div className="text-xs text-dark-text-muted">
              Invited {formatDate(invitation.created_at)} • {getExpiryText()}
            </div>
          </div>
        </div>
      </div>

      {/* Allocated quotas */}
      <div className="bg-dark-bg/30 border border-dark-border/30 rounded-lg p-4">
        <div className="text-xs font-semibold text-dark-text-muted mb-2">
          Allocated Quotas:
        </div>
        <div className="space-y-1 text-sm text-dark-text">
          <div className="flex items-center gap-2">
            <span className="text-dark-text-muted">•</span>
            <span>Clipboard slots: Pending quota data</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-dark-text-muted">•</span>
            <span>Conversations: Pending quota data</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-dark-text-muted">•</span>
            <span>AI requests: Pending quota data</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      {!isExpired && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => onCopyLink(invitationUrl)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-dark-bg/50 border border-dark-border
                     rounded-lg hover:bg-dark-bg hover:border-accent-blue
                     transition-colors"
          >
            <ClipboardDocumentIcon className="w-4 h-4" />
            Copy Link
          </button>

          <button
            onClick={() => onCancel(invitation.id)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-dark-bg/50 border border-dark-border
                     rounded-lg hover:bg-red-500/20 hover:border-red-500/50
                     transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {isExpired && (
        <div className="flex gap-2">
          <button
            onClick={() => onCancel(invitation.id)}
            className="px-4 py-2 text-sm text-dark-text-muted bg-dark-bg/30 border border-dark-border/30
                     rounded-lg cursor-not-allowed"
            disabled
          >
            Expired
          </button>
        </div>
      )}
    </div>
  );
}

export default function InvitationList({
  invitations,
  loading = false,
  onCancelInvitation,
  onCopyLink,
}: InvitationListProps) {
  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-dark-card/50 border border-dark-border/50 rounded-lg p-5 h-40 animate-pulse"
          />
        ))}
      </div>
    );
  }

  // Empty state
  if (invitations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 flex items-center justify-center text-dark-text-muted">
          <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-dark-text mb-2">No Pending Invitations</h3>
        <p className="text-sm text-dark-text-muted text-center max-w-sm">
          All invitations have been accepted or expired.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-dark-text-muted">
          {invitations.length} pending {invitations.length === 1 ? 'invitation' : 'invitations'}
        </div>
      </div>

      {/* Invitation cards */}
      <div className="space-y-4">
        {invitations.map((invitation) => (
          <InvitationCard
            key={invitation.id}
            invitation={invitation}
            onCancel={onCancelInvitation}
            onCopyLink={onCopyLink}
          />
        ))}
      </div>
    </div>
  );
}
